import { useContext } from 'react'
import { observer } from 'mobx-react-lite'
import { Outlet } from 'react-router-dom'
import { Headset } from 'lucide-react'
import { PageHeaderComponent } from 'app/components/page-header/PageHeaderComponent'
import { Toggle } from 'app/components/ui/toggle'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'app/components/ui/tooltip'
import { Button } from 'app/components/ui/button'
import type { TabType } from 'app/components/ui/nav-tabs'
import { PAGE_GUTTER, PageGutterContext } from 'app/components/layout/PageScroll'
import { cn } from 'app/lib/utils'

export type AIAgentLocationComponentProps = {
  locationName: string
  tabs: TabType[]
  isAgentLive: boolean
  testPhoneNumber: string | undefined
  onToggleAgentLive: (value: boolean) => Promise<boolean>
}

export const AIAgentLocationComponent = observer(function AIAgentLocationComponent({
  locationName,
  tabs,
  isAgentLive,
  testPhoneNumber,
  onToggleAgentLive,
}: AIAgentLocationComponentProps) {
  const gutter = useContext(PageGutterContext)
  return (
    // Outside the settings well, `main` already carries the page gutter and top padding; inside
    // the well `main`'s gutter is stripped, so this wrapper takes over both only in that context.
    // The side gutter goes inside the scroll area, as in PageScroll: on the outside it put the
    // scroll area's clipping edge exactly on the cards' right borders and cut them off.
    <div className={cn(`flex h-full flex-col`, gutter === `inset` && `pt-8`)} data-testid="agent-location-page">
      <div className={cn(`shrink-0`, gutter === `inset` && PAGE_GUTTER.inset)}>
        <PageHeaderComponent
          title={locationName}
          tabs={tabs}
          primaryButtonSlot={
            <Toggle
              defaultValue={isAgentLive}
              label="AI Agent Live"
              handleToggle={onToggleAgentLive}
              controlled
              labelClassName="text-strong text-body-sm"
            />
          }
          secondaryButtonSlot={
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="white"
                    size="sm"
                    className="my-1 mr-1 cursor-help"
                    disabled
                    data-testid="agent-test-number"
                  >
                    <Headset className="h-4 w-4" />
                    <span>{testPhoneNumber}</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">Agent Test Number</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          }
        />
      </div>
      <div className={cn(`mt-2 flex grow flex-col overflow-auto`, gutter === `inset` && PAGE_GUTTER.inset)}>
        <Outlet />
      </div>
    </div>
  )
})
